import { useEffect } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing4, Spacing8, Spacing24 } from 'autogen/design-tokens/tokens';
import { FrontPageSectionContentDiscriminator } from 'autogen/swagger/all';
import { usePersonalOffersForPlaceholderQuery } from 'autogen/swagger/all/hooks';
import {
    LoadingTitle,
    LoginButtonLabel,
    SubTitle,
    TagTitle,
    Title
} from 'autogen/translation-keys/trans-website-personaloffer-offersloginnudge';
import LinkActionButton from 'components/Button/LinkActionButton';
import SkeletonSlider from 'components/ContentCard/components/SkeletonSlider';
import type { ImageAspectRatio } from 'components/ContentCard/types';
import Tag from 'components/generic/tag';
import { Typography } from 'components/generic/typography';
import Layout from 'components/Layout';
import Flex from 'components/Layout/components/Flex';
import PersonalOfferSection from 'components/pages/MyProfile/FrontPage/components/Sections/PersonalOfferSection';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import useLoginLink from 'hooks/user/useLoginLink';
import ThemeProvider from 'theming/ThemeProvider';
import { breakpoint } from 'variables';

const FullWidthWrapper = styled.div`
    background-color: ${({ theme }) => theme.ColorBackgroundPrimary};
    padding-top: ${({ theme }) => theme.Spacing40};
    padding-bottom: ${({ theme }) => theme.Spacing40};
`;

const Wrapper = styled.div`
    padding: ${({ theme }) => theme.Spacing24};
    background-color: ${({ theme }) => theme.ColorBackgroundPrimary};

    ${breakpoint.up('1025px')} {
        padding: ${({ theme }) => `${theme.Spacing24} ${theme.Spacing32}`};
    }
`;

const StyledHeadline = styled(Typography.Headline)`
    color: ${({ theme }) => theme.ColorTextPrimary};
`;

const StyledSubTitle = styled(Typography.Body)`
    color: ${({ theme }) => theme.ColorTextPrimary};
`;

function PersonalOfferCardsRenderer() {
    const isAuthenticated = useIsAuthenticated(false);
    const { data, isFetching, refetch } = usePersonalOffersForPlaceholderQuery({
        enabled: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 0
    });
    const { loginLinkAction } = useLoginLink();
    const { t } = useTranslation();

    const model = data || undefined;

    useEffect(() => {
        if (isAuthenticated) {
            refetch();
        }
    }, [isAuthenticated, refetch]);

    if (!isAuthenticated) {
        return (
            <ThemeProvider theme={'club1'}>
                <Wrapper>
                    <Flex.Row gap={Spacing24} justifyContent="space-between" alignItems="center">
                        <Flex.Column gap={Spacing8}>
                            <Flex.Row gap={Spacing8}>
                                <Tag>{t(TagTitle)}</Tag>
                            </Flex.Row>
                            <Flex.Column gap={Spacing4}>
                                <StyledHeadline size="L">{t(Title)}</StyledHeadline>
                                <StyledSubTitle size="L">{t(SubTitle)}</StyledSubTitle>
                            </Flex.Column>
                        </Flex.Column>
                        <Flex.Column>
                            {loginLinkAction && (
                                <LinkActionButton action={loginLinkAction} size="lg">
                                    {t(LoginButtonLabel)}
                                </LinkActionButton>
                            )}
                        </Flex.Column>
                    </Flex.Row>
                </Wrapper>
            </ThemeProvider>
        );
    }

    if (!model && isFetching) {
        return (
            <ThemeProvider theme={'club1'}>
                <FullWidthWrapper>
                    <Layout.Section heading={t(LoadingTitle)}>
                        <ThemeProvider theme={'default'}>
                            <SkeletonSlider skeletonProps={{ aspectRatio: '800/930' as ImageAspectRatio }} />
                        </ThemeProvider>
                    </Layout.Section>
                </FullWidthWrapper>
            </ThemeProvider>
        );
    }

    if (!model) {
        return null;
    }

    return (
        <ThemeProvider theme={'club1'}>
            <FullWidthWrapper>
                <Layout.Section heading={model.header?.name}>
                    {FrontPageSectionContentDiscriminator(
                        model.content,
                        () => null,
                        () => null,
                        () => null,
                        () => null,
                        () => null,
                        () => null,
                        () => null,
                        () => null,
                        () => null,
                        () => null,
                        (personalOfferModel) => (
                            <ThemeProvider theme={'default'}>
                                <PersonalOfferSection model={personalOfferModel} />
                            </ThemeProvider>
                        ),
                        () => null,
                        () => null,
                        () => null,
                        () => null,
                        () => null,
                        () => null,
                        () => null
                    )}
                </Layout.Section>
            </FullWidthWrapper>
        </ThemeProvider>
    );
}

export default withErrorBoundary(PersonalOfferCardsRenderer, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
